"use client";

import { useState, useEffect, useCallback } from "react";
import {
  STORAGE_KEYS,
  CONSENT_EXPIRY_DAYS,
  type ConsentState,
} from "@/lib/constants";

interface UseConsentReturn {
  consent: ConsentState | null;
  hasConsented: boolean;
  isExpired: boolean;
  updateConsent: (analytics: boolean, ads: boolean) => void;
  acceptAll: () => void;
  rejectAll: () => void;
}

function isConsentExpired(timestamp: number): boolean {
  const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - timestamp > expiryMs;
}

function syncWithGtag(analytics: boolean, ads: boolean): void {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("consent", "update", {
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
    analytics_storage: analytics ? "granted" : "denied",
  });
}

export function useConsent(): UseConsentReturn {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [hasConsented, setHasConsented] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.CONSENT);
      if (stored) {
        const parsed: ConsentState = JSON.parse(stored);
        if (isConsentExpired(parsed.timestamp)) {
          setIsExpired(true);
          setHasConsented(false);
        } else {
          setConsent(parsed);
          setHasConsented(true);
          syncWithGtag(parsed.analytics, parsed.ads);
        }
      }
    } catch {
      // localStorage not available or invalid data
    }
  }, []);

  const updateConsent = useCallback((analytics: boolean, ads: boolean) => {
    const newConsent: ConsentState = {
      analytics,
      ads,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEYS.CONSENT, JSON.stringify(newConsent));
    } catch {
      // localStorage not available
    }

    setConsent(newConsent);
    setHasConsented(true);
    setIsExpired(false);
    syncWithGtag(analytics, ads);
  }, []);

  const acceptAll = useCallback(() => {
    updateConsent(true, true);
  }, [updateConsent]);

  const rejectAll = useCallback(() => {
    updateConsent(false, false);
  }, [updateConsent]);

  return {
    consent,
    hasConsented,
    isExpired,
    updateConsent,
    acceptAll,
    rejectAll,
  };
}
